"use client";

import { useState } from "react";
import Image from "next/image";
import { IconPlayerPlayFilled, IconVideo } from "@tabler/icons-react";
import {
  getVideoEmbedUrl,
  getVideoThumbnailUrl,
  getVideoProvider,
} from "@/utils/video";
import { canOptimizeImage } from "@/lib/image";
import { cn } from "@/lib/utils";
import type { EventVideo } from "./types";
import { BlockTitle, UI, useText } from "./shared";

/** Shorts are vertical; everything else is assumed landscape unless set. */
function isPortrait(video: EventVideo) {
  if (video.orientation) return video.orientation === "portrait";
  return /youtube\.com\/shorts\//i.test(video.url);
}

/** Video cards framed to each video's own shape, 2 → 3 → 4 columns. Players load inline only after the play icon is clicked. */
export function EventVideoGallery({
  items,
  title,
  initial = 5,
  showTitle = true,
  featured = false,
}: {
  items: EventVideo[];
  title?: string;
  initial?: number;
  showTitle?: boolean;
  featured?: boolean;
}) {
  const { text } = useText();
  const [visible, setVisible] = useState(initial);
  const [playing, setPlaying] = useState<number | null>(null);
  if (items.length === 0) return null;

  return (
    <div>
      {showTitle && (
        <BlockTitle icon={<IconVideo className="size-5" aria-hidden="true" />}>
          {title ?? text(UI.videoHighlights)}
        </BlockTitle>
      )}
      <ul
        className={
          featured
            ? "mx-auto grid w-full max-w-3xl grid-cols-1 justify-items-center gap-5"
            : "grid grid-cols-2 items-start gap-3 md:grid-cols-3 md:gap-4 xl:grid-cols-4 xl:gap-5"
        }
      >
        {items.slice(0, visible).map((video, i) => (
          <li
            key={`${video.url}-${i}`}
            className={cn("w-full", featured && isPortrait(video) && "max-w-[270px]")}
          >
            <EventVideoCard
              video={video}
              playing={playing === i}
              onPlay={() => setPlaying(i)}
              featured={featured}
            />
          </li>
        ))}
      </ul>
      {visible < items.length && (
        <div className="mt-6 flex justify-center">
          <button
            type="button"
            onClick={() => setVisible((v) => v + initial)}
            className="rounded-full border border-[var(--event-accent)] px-6 py-2.5 text-sm font-semibold text-[var(--event-accent)] transition hover:bg-[#f6cf63]/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f6cf63]"
          >
            {text(UI.viewMore)}
          </button>
        </div>
      )}
    </div>
  );
}

export function EventVideoCard({
  video,
  playing,
  onPlay,
  featured = false,
}: {
  video: EventVideo;
  playing: boolean;
  onPlay: () => void;
  featured?: boolean;
}) {
  const { text } = useText();
  const thumbnail = video.thumbnail || getVideoThumbnailUrl(video.url);
  const title = text(video.title);
  const src = getVideoEmbedUrl(video.url);
  const portrait = isPortrait(video);
  return (
    <div className="group block w-full overflow-hidden rounded-[14px] border border-white/10 bg-[#0a1f4f] text-left transition hover:border-[#f6cf63]/40">
      <div
        className={cn(
          "relative overflow-hidden bg-black",
          portrait ? "aspect-[9/16]" : "aspect-video",
        )}
      >
        {playing && src ? (
          <iframe
            src={src}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 size-full"
          />
        ) : (
          <>
            {thumbnail && (
              <Image
                src={thumbnail}
                unoptimized={!canOptimizeImage(thumbnail)}
                alt=""
                fill
                sizes={
                  featured
                    ? portrait
                      ? "270px"
                      : "(min-width: 768px) 768px, 100vw"
                    : "(min-width: 1280px) 293px, (min-width: 768px) 33vw, 50vw"
                }
                className="object-cover transition duration-500 ease-out motion-safe:group-hover:scale-105"
              />
            )}
            <span className="absolute inset-0 bg-[linear-gradient(180deg,rgba(6,24,62,0.05),rgba(3,16,45,0.55))]" />
            <span className="absolute inset-0 flex items-center justify-center">
              {src ? (
                <button
                  type="button"
                  onClick={onPlay}
                  aria-label={`${text(UI.play)}: ${title}`}
                  className="flex size-11 items-center justify-center rounded-full bg-[#f6cf63] text-[#041127] shadow-[0_10px_30px_rgba(0,0,0,0.35)] transition hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white md:size-12"
                >
                  <IconPlayerPlayFilled
                    className="ml-0.5 size-5"
                    aria-hidden="true"
                  />
                </button>
              ) : (
                <a
                  href={video.url}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`${text(UI.play)}: ${title}`}
                  className="flex size-11 items-center justify-center rounded-full bg-[#f6cf63] text-[#041127] shadow-[0_10px_30px_rgba(0,0,0,0.35)] transition hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white md:size-12"
                >
                  <IconPlayerPlayFilled
                    className="ml-0.5 size-5"
                    aria-hidden="true"
                  />
                </a>
              )}
            </span>
          </>
        )}
      </div>
      {/* No visible caption: CMS titles are placeholders ("Event-Video1"…).
          The title is still used for the iframe and play-button labels. */}
      {playing && getVideoProvider(video.url) === "facebook" && (
        <div className="flex justify-end px-3 py-2.5">
          <a
            href={video.url}
            target="_blank"
            rel="noreferrer"
            className="shrink-0 text-xs text-[#f6cf63] underline"
          >
            {text({ km: "មើលនៅលើ Facebook", en: "Watch on Facebook" })}
          </a>
        </div>
      )}
    </div>
  );
}
